import { Badge, Box, Flex, Input, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { FaPlus, FaTimes } from "react-icons/fa"

import { ContentService, type TopicCreate } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import { toaster } from "@/components/ui/toaster"
import { handleError } from "@/utils"

interface TopicSelectorProps {
  selectedTopics: Array<{ id: string; name: string }>
  onChange: (topics: Array<{ id: string; name: string }>) => void
  allowCreate?: boolean // Optional prop to control topic creation, defaults to true
}

export function TopicSelector({
  selectedTopics,
  onChange,
  allowCreate = true, // Default to true for backward compatibility
}: TopicSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const queryClient = useQueryClient()

  // Debounce search query by 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Use autocomplete endpoint for searching topics
  const { data: autocompleteResults } = useQuery({
    queryKey: ["topics-autocomplete", debouncedSearchQuery],
    queryFn: () =>
      ContentService.autocompleteTopics({ q: debouncedSearchQuery.trim() }),
    enabled: showSuggestions, // Only fetch when dropdown is open
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Create topic mutation
  const createTopicMutation = useMutation({
    mutationFn: (data: TopicCreate) =>
      ContentService.createTopic({ requestBody: data }),
    onSuccess: (newTopic) => {
      queryClient.invalidateQueries({ queryKey: ["topics-autocomplete"] })
      onChange([...selectedTopics, { id: newTopic.id, name: newTopic.name }])
      setSearchQuery("")
      setShowSuggestions(false)
      toaster.success({
        title: "Topic created",
        description: `"${newTopic.name}" has been created and added to the question`,
      })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const allTopics = autocompleteResults || []

  // Filter out already selected topics
  const filteredTopics = allTopics.filter(
    (topic) => !selectedTopics.some((selected) => selected.id === topic.id),
  )

  // Check if search query matches any existing topic (exact match)
  const exactMatch = allTopics.find(
    (topic) => topic.name.toLowerCase() === searchQuery.trim().toLowerCase(),
  )

  const handleAddTopic = (topic: { id: string; name: string }) => {
    onChange([...selectedTopics, topic])
    setSearchQuery("")
    setShowSuggestions(false)
  }

  const handleRemoveTopic = (topicId: string) => {
    onChange(selectedTopics.filter((topic) => topic.id !== topicId))
  }

  const handleCreateTopic = () => {
    if (searchQuery.trim() && !exactMatch) {
      createTopicMutation.mutate({ name: searchQuery.trim() })
    }
  }

  return (
    <Box width="100%">
      {/* Search Input */}
      <Box position="relative">
        <Input
          placeholder={
            allowCreate
              ? "Search or click to view topics, or create new..."
              : "Search or click to view topics..."
          }
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") {
              return
            }

            event.preventDefault()
            event.stopPropagation()

            if (filteredTopics.length > 0) {
              handleAddTopic(filteredTopics[0])
              return
            }

            if (allowCreate && !exactMatch && searchQuery.trim()) {
              handleCreateTopic()
            }
          }}
          required={false}
        />

        {/* Suggestions Dropdown */}
        {showSuggestions && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            _dark={{ bg: "gray.800" }}
            borderWidth="1px"
            borderRadius="md"
            boxShadow="lg"
            maxH="300px"
            overflowY="auto"
            zIndex={10}
          >
            <VStack align="stretch" gap={0}>
              {/* Existing Topics */}
              {filteredTopics.length > 0 && (
                <>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    px={3}
                    py={2}
                    borderBottomWidth="1px"
                  >
                    {searchQuery.trim()
                      ? "Matching Topics"
                      : "Available Topics"}
                  </Text>
                  {filteredTopics.map((topic) => (
                    <Box
                      key={topic.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: "gray.100", _dark: { bg: "gray.700" } }}
                      onClick={() => handleAddTopic(topic)}
                    >
                      <Text>{topic.name}</Text>
                    </Box>
                  ))}
                </>
              )}

              {/* Create New Topic */}
              {allowCreate && !exactMatch && searchQuery.trim() && (
                <>
                  {filteredTopics.length > 0 && <Box borderTopWidth="1px" />}
                  <Box
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg="blue.50"
                    _dark={{ bg: "blue.900" }}
                    _hover={{ bg: "blue.100", _dark: { bg: "blue.800" } }}
                    onClick={handleCreateTopic}
                  >
                    <Flex align="center" gap={2}>
                      <FaPlus />
                      <Text fontWeight="semibold">
                        Create "{searchQuery.trim()}"
                      </Text>
                    </Flex>
                  </Box>
                </>
              )}

              {/* No Results */}
              {filteredTopics.length === 0 && !searchQuery.trim() && (
                <Box px={3} py={2}>
                  <Text color="gray.500">No topics available.</Text>
                </Box>
              )}
              {filteredTopics.length === 0 &&
                searchQuery.trim() &&
                !exactMatch && (
                  <Box px={3} py={2}>
                    <Text color="gray.500">
                      {allowCreate
                        ? "No matching topics found. You can create a new one above."
                        : "No matching topics found."}
                    </Text>
                  </Box>
                )}
            </VStack>
          </Box>
        )}
      </Box>

      {/* Click outside to close */}
      {showSuggestions && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={9}
          onClick={() => setShowSuggestions(false)}
        />
      )}

      {/* Selected Topics */}
      {selectedTopics.length > 0 && (
        <Flex gap={2} wrap="wrap" mt={3}>
          {selectedTopics.map((topic) => (
            <Badge
              key={topic.id}
              colorPalette="green"
              size="lg"
              py={1}
              px={2}
              display="flex"
              alignItems="center"
              gap={2}
            >
              {topic.name}
              <button
                type="button"
                onClick={() => handleRemoveTopic(topic.id)}
                style={{ cursor: "pointer", opacity: 1 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.7"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1"
                }}
              >
                <FaTimes />
              </button>
            </Badge>
          ))}
        </Flex>
      )}
    </Box>
  )
}
